import { Page } from '@ui/themed';
import { t } from 'i18next';
import { useMemo } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { CUSTOMER_GROUP_BASE_SLUG, INITIAL_CUSTOMER_GROUP, SETTINGS_ORDER } from '../constants';
import { useLastCustomerGroupCreateMethod } from '../create-method';
import { handleCustomerGroupBuilderAxiosError } from '../handle-errors';
import { useCreateCustomerGroup } from '../hooks/use-create-customer-group';
import {
  CustomerGroupBuilderContext,
  useCustomerGroupBuilderFormLogic,
} from '../hooks/use-customer-group-builder-form';
import { useCustomerGroups } from '../hooks/use-customer-groups';
import { useUpdateCustomerGroup } from '../hooks/use-update-customer-group';
import { useUpdateCustomerGroupSettings } from '../hooks/use-update-customer-group-settings';
import { parseQuickSetupDraft } from '../quick-setup-to-builder';
import { transformSetting } from '../transformers';
import type {
  CustomerGroupBuilderSettingsState,
  CustomerGroupBuilderValidated,
} from '../types/customer-group.schema';
import type { SettingKey } from '../types/settings.types';
import { CustomerGroupsBuilderContent } from './customer-groups-builder-content';

export const CustomerGroupsBuilderPage = withPageProviders(CustomerGroupsBuilderPageWrapper);
function CustomerGroupsBuilderPageWrapper() {
  const { navigationHandler, notificationHandler } = useHandlers();
  const [, setLastCustomerGroupCreateMethod] = useLastCustomerGroupCreateMethod();
  const { data: customerGroups, isLoading, isError, refetch } = useCustomerGroups();
  const { slug: existingSlug } = navigationHandler.params();
  const quickSetupDraft = existingSlug
    ? undefined
    : parseQuickSetupDraft(
        navigationHandler.query(),
        customerGroups.map((group) => group.slug),
      );
  const { mutateAsync: updateCustomerGroup } = useUpdateCustomerGroup();
  const { mutateAsync: createCustomerGroup } = useCreateCustomerGroup();
  const { mutateAsync: updateCustomerGroupSettings } = useUpdateCustomerGroupSettings();

  const initialState = useMemo(() => {
    const currentCustomerGroup = customerGroups.find((group) => group.slug === existingSlug);

    if (!currentCustomerGroup && existingSlug) {
      return undefined;
    }

    if (!currentCustomerGroup) {
      return INITIAL_CUSTOMER_GROUP;
    }

    // Order Settings to be consistent for creation and editing
    const orderedSettings = [...currentCustomerGroup.settings].sort(
      (a, b) =>
        SETTINGS_ORDER.indexOf(a.key as SettingKey) - SETTINGS_ORDER.indexOf(b.key as SettingKey),
    );

    const initialSettings: CustomerGroupBuilderSettingsState = Object.fromEntries(
      orderedSettings.map((item) => {
        // handle addressManagement specific data as they belong to a card
        if (item.key === 'allowAddressEditing') {
          const billingValue = orderedSettings.find(
            (value) => value.key === 'allowDefaultBillingModification',
          )?.value;

          if (
            (billingValue === null || billingValue === undefined) &&
            (item.value === null || item.value === undefined)
          ) {
            return ['allowAddressEditing', undefined];
          }

          return [
            'allowAddressEditing',
            {
              allowAddressEditing: item.value,
              allowDefaultBillingModification: billingValue ?? false,
            },
          ];
        }

        return transformSetting(item);
      }),
    );

    const hasChildren = customerGroups.some(
      (group) => group.parent_slug === currentCustomerGroup?.slug,
    );

    return {
      ...currentCustomerGroup,
      hasChildren,
      settings: initialSettings,
    };
  }, [existingSlug, customerGroups]);

  async function handleSubmit(payload: CustomerGroupBuilderValidated) {
    const isEditing = !!existingSlug;
    try {
      if (payload.slug !== CUSTOMER_GROUP_BASE_SLUG) {
        const mutation = isEditing ? updateCustomerGroup(payload) : createCustomerGroup(payload);
        await mutation;
      }

      await updateCustomerGroupSettings(
        { slug: payload.slug, settings: payload.settings },
        {
          onSuccess: () => {
            notificationHandler.success(
              t(
                `features:customer_group.pages.customer_group_builder_page.notifications.success.${isEditing ? 'update' : 'create'}_succeeded`,
                { name: payload.name },
              ),
            );

            navigationHandler.navigate('/customers/groups');
          },
        },
      );

      if (!isEditing) {
        setLastCustomerGroupCreateMethod('advanced');
      }
    } catch (err) {
      handleCustomerGroupBuilderAxiosError(err, notificationHandler, isEditing);
      // throw so the useFormProps can catch it to check if the submission was successful
      throw err;
    }
  }

  const form = useCustomerGroupBuilderFormLogic({
    initialState: initialState,
    draftState: quickSetupDraft,
    onSubmit: handleSubmit,
    onValidationError: ({ formApi }) => {
      notificationHandler.error(
        `${t(
          `features:customer_group.pages.customer_group_builder_page.notifications.errors.${existingSlug ? 'update' : 'create'}_failed`,
        )} ${formApi.state.errors.length !== 0 ? t('features:customer_group.pages.customer_group_builder_page.notifications.errors.validation_failed', { number: formApi.state.errors.length }) : ''}`,
      );
    },
  });

  if (isLoading || !initialState) {
    return (
      <Page narrow>
        <SparkSuspense
          isError={isError || (!isLoading && !initialState)}
          errorMessage={t('errors:generic')}
          onReload={() => void refetch()}
          isLoading={isLoading}
        />
      </Page>
    );
  }

  return (
    <CustomerGroupBuilderContext.Provider value={form}>
      <CustomerGroupsBuilderContent editSlug={existingSlug} />
    </CustomerGroupBuilderContext.Provider>
  );
}
